import { type ApiSchemas, call } from '@rental/contracts/client'
import { format, formatDateTime } from '@rental/i18n'
import { Field } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type RefObject, useLayoutEffect, useState } from 'react'
import { api, load } from '../api'
import { ferryWords } from '../components/BookingItem'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { ProblemNotice, StaffInput, StaffSelect, StaffTextarea } from '../components/ui'
import { useSend } from '../fines/send'
import { useLocale } from '../i18n'
import { problemOf } from '../problems'
import { useTripWords } from './TripParts'
import { type Trip, type TripForm, taskRequest, tripFerry, tripForm } from './trips'

type BookingRow = ApiSchemas['StaffBookingRow']

/** A trip the office opened to write on, with the booking it belongs to. */
export type AskedTrip = { trip: Trip; booking: BookingRow }

const PEOPLE_QUERY_KEY = ['delivery-people'] as const

/**
 * S9 (LOC-5, J8): the office writes a trip whole, the version it read with it
 * (deliveries.manage): who drives it, where they meet the customer, a note,
 * and the ferry the customer turns out to arrive on. Shared by Today and the
 * booking page: it opens each time it is handed a trip asked, once that trip
 * is drawn, so the focus lands on its first field. A refusal is said in words
 * and the question stays; a trip changed meanwhile is read again for the next try.
 */
export function TripDialog({
  dialogRef,
  asked,
  onSaved,
}: {
  dialogRef: RefObject<HTMLDialogElement | null>
  asked: AskedTrip | null
  /** After the dialog has closed: the trip as the server now has it. */
  onSaved: (trip: Trip, booking: BookingRow) => void
}) {
  const { locale, messages: m } = useLocale()
  const copy = m.trips.form
  const queryClient = useQueryClient()
  const tripWords = useTripWords()
  const { send, busy, problem, clear } = useSend()
  // Each opening starts from the trip as it was read, whatever was typed last time.
  const [opened, setOpened] = useState<AskedTrip | null>(null)
  const [form, setForm] = useState<TripForm | null>(null)
  const [badTime, setBadTime] = useState(false)
  if (asked !== opened) {
    setOpened(asked)
    setForm(asked ? tripForm(asked.trip) : null)
    setBadTime(false)
  }
  useLayoutEffect(() => {
    if (asked && !dialogRef.current?.open) dialogRef.current?.showModal()
  }, [asked, dialogRef])
  const people = useQuery({
    queryKey: PEOPLE_QUERY_KEY,
    queryFn: () => load(call(api.GET('/v1/staff/deliveries/people'))),
    enabled: asked !== null,
  })

  const set = (field: keyof TripForm) => (value: string) =>
    setForm((current) => (current ? { ...current, [field]: value } : current))

  async function save() {
    if (!asked || !form) return
    const request = taskRequest(asked.trip, form)
    if (!request.ok) {
      setBadTime(true)
      return
    }
    const { code } = asked.booking
    const answer = await send(() =>
      call(
        api.PUT('/v1/staff/bookings/{code}/deliveries/{leg}', {
          params: { path: { code, leg: asked.trip.leg } },
          body: request.body,
        }),
      ),
    )
    // Saved or refused, the board and the booking read the trip again: a refusal is most
    // often a trip changed meanwhile, or a person who can no longer be given one.
    void queryClient.invalidateQueries({ queryKey: ['today'] })
    void queryClient.invalidateQueries({ queryKey: ['booking', code.toUpperCase()] })
    if (!answer) {
      void queryClient.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY })
      return
    }
    dialogRef.current?.close()
    onSaved(answer, asked.booking)
  }

  const trip = asked?.trip ?? null
  const booking = asked?.booking ?? null
  const current = trip?.task?.assignedTo ?? null
  const listed = people.data?.items ?? []
  const roles = m.roles as Readonly<Record<string, string>>
  const bookingFerry = trip && booking ? tripFerry({ ...trip, task: null }, booking) : null
  // A collection meets no arrival: its ferry fields show only where one was written on it.
  const showsFerry =
    trip !== null &&
    (trip.leg === 'delivery' || trip.task?.ferryName != null || trip.task?.ferryArrivalAt != null)

  return (
    <ConfirmDialog
      dialogRef={dialogRef}
      title={trip && booking ? tripWords(trip.leg, booking) : ''}
      confirmLabel={copy.save}
      cancelLabel={copy.cancel}
      busy={busy}
      ready={!badTime}
      reasons={[copy.ferryTimeInvalid]}
      onConfirm={() => void save()}
      onClose={clear}
    >
      {trip && booking && form ? (
        <>
          <p>
            {format(copy.about, {
              code: booking.code,
              customer: booking.customerName,
              when: formatDateTime(locale, trip.dueAt),
            })}
          </p>
          <Field label={copy.driver} hint={copy.driverHint}>
            {(control) => (
              <StaffSelect
                {...control}
                value={form.assignedTo}
                aria-busy={people.isPending}
                onChange={(event) => set('assignedTo')(event.target.value)}
              >
                <option value="">{copy.nobody}</option>
                {listed.map((person) => (
                  <option key={person.id} value={person.id}>
                    {`${person.displayName} (${roles[person.role] ?? person.role})`}
                  </option>
                ))}
                {/* Whoever drives it now stays a choice while the list loads, or if it fails. */}
                {current && !listed.some((person) => person.id === current.id) ? (
                  <option value={current.id}>{current.displayName}</option>
                ) : null}
              </StaffSelect>
            )}
          </Field>
          <ProblemNotice problem={problemOf(people.error)} quiet />
          <Field label={copy.meetingPoint} hint={copy.meetingPointHint}>
            {(control) => (
              <StaffInput
                {...control}
                maxLength={200}
                autoComplete="off"
                value={form.meetingPoint}
                onChange={(event) => set('meetingPoint')(event.target.value)}
              />
            )}
          </Field>
          <Field label={copy.note}>
            {(control) => (
              <StaffTextarea
                {...control}
                maxLength={1000}
                rows={3}
                value={form.note}
                onChange={(event) => set('note')(event.target.value)}
              />
            )}
          </Field>
          {showsFerry ? (
            <>
              {bookingFerry ? (
                <p className="text-sm text-text-muted">
                  {format(copy.bookingFerry, { ferry: ferryWords(bookingFerry, locale, m) })}
                </p>
              ) : null}
              <Field label={copy.ferryName} hint={copy.ferryNameHint}>
                {(control) => (
                  <StaffInput
                    {...control}
                    maxLength={100}
                    autoComplete="off"
                    value={form.ferryName}
                    onChange={(event) => set('ferryName')(event.target.value)}
                  />
                )}
              </Field>
              <Field
                label={copy.ferryTime}
                hint={copy.ferryTimeHint}
                error={badTime ? copy.ferryTimeInvalid : null}
              >
                {(control) => (
                  <StaffInput
                    {...control}
                    type="time"
                    value={form.ferryTime}
                    onChange={(event) => {
                      // A time half typed reads as empty: said, never sent as "no time".
                      setBadTime(event.target.validity.badInput)
                      set('ferryTime')(event.target.value)
                    }}
                  />
                )}
              </Field>
            </>
          ) : null}
          <ProblemNotice problem={problem} />
        </>
      ) : null}
    </ConfirmDialog>
  )
}
